/**
 * ====================================================================
 * FIKO CAD LAYOUT MANAGER
 * ====================================================================
 *
 * Dimension yerleşim yöneticisi.
 * Overlap, occlusion ve placement sorunlarını çözer.
 *
 * Özellikler:
 * - Çakışma algılama (overlap detection)
 * - Otomatik offset ayarlama
 * - Occlusion kontrolü (geometri tarafından kapatılma)
 * - Akıllı yerleşim önerileri
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import {
    Dimension,
    DimensionBounds,
    OverlapResult,
    PlacementSuggestion,
    FallbackInfo,
    TextPlacement
} from './DimensionTypes';
import type { DimensionManager } from './DimensionManager';

/**
 * Layout check result
 */
export interface LayoutCheckResult {
    valid: boolean;
    overlaps: OverlapResult;
    occluded: boolean;
    fallback?: FallbackInfo;
}

/**
 * Layout Manager - Yerleşim yöneticisi
 */
export class LayoutManager {
    // Manager referansı
    private manager: DimensionManager;

    // Önbellek
    private boundsCache: Map<string, DimensionBounds> = new Map();
    private lastCameraPosition: THREE.Vector3 = new THREE.Vector3();

    // Ayarlar
    private readonly OVERLAP_THRESHOLD = 5;  // px cinsinden minimum mesafe
    private readonly OCCLUSION_SAMPLES = 8;  // Occlusion kontrolü için örnek sayısı
    private readonly MAX_OFFSET_ITERATIONS = 5;  // Otomatik offset denemesi

    constructor(manager: DimensionManager) {
        this.manager = manager;
    }

    /**
     * Dimension yerleşimini kontrol et ve gerekirse çöz
     */
    checkAndResolve(dimension: Dimension): LayoutCheckResult {
        const result: LayoutCheckResult = {
            valid: true,
            overlaps: { overlaps: false },
            occluded: false
        };

        // 1. Overlap kontrolü
        const overlaps = this.checkOverlaps(dimension);
        result.overlaps = overlaps;

        if (overlaps.overlaps) {
            // Otomatik offset ile çöz
            const resolved = this.resolveOverlap(dimension, overlaps);
            if (!resolved) {
                result.fallback = {
                    applied: true,
                    reason: 'Overlap çözülemedi, alternatif yerleşim uygulandı'
                };
            }
        }

        // 2. Occlusion kontrolü
        const occluded = this.checkOcclusion(dimension);
        result.occluded = occluded;

        if (occluded) {
            // Leader line ile çöz veya alternatif yerleşim
            const resolved = this.resolveOcclusion(dimension);
            if (!resolved) {
                result.fallback = {
                    applied: true,
                    reason: 'Dimension geometri tarafından kapatılıyor, leader eklendi'
                };
            }
        }

        result.valid = !overlaps.overlaps && !occluded;
        return result;
    }

    /**
     * Overlap kontrolü - Diğer dimension'larla çakışma
     */
    checkOverlaps(dimension: Dimension): OverlapResult {
        const bounds = this.getBounds(dimension);
        if (!bounds) {
            return { overlaps: false };
        }

        const overlappingWith: Dimension[] = [];

        // Tüm dimension'ları kontrol et
        for (const other of this.manager.getAllDimensions()) {
            if (other === dimension) continue;

            const otherBounds = this.getBounds(other);
            if (!otherBounds) continue;

            if (this.boundsOverlap(bounds, otherBounds)) {
                overlappingWith.push(other);
            }
        }

        if (overlappingWith.length === 0) {
            return { overlaps: false };
        }

        // Severity belirle
        let severity: 'minor' | 'major' | 'critical' = 'minor';
        if (overlappingWith.length > 2) {
            severity = 'critical';
        } else if (overlappingWith.length > 0) {
            severity = 'major';
        }

        return {
            overlaps: true,
            overlappingWith,
            severity
        };
    }

    /**
     * Occlusion kontrolü - Geometri tarafından kapatılma
     */
    checkOcclusion(dimension: Dimension): boolean {
        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera || !scene.scene) {
            return false;
        }

        const camera = scene.anaGosterim.aktifKamera;

        // Dimension'ın anahtar noktalarını al
        const points = this.getDimensionKeyPoints(dimension);
        if (points.length === 0) {
            return false;
        }

        // Her nokta için raycast yap
        const raycaster = new THREE.Raycaster();
        let occludedCount = 0;

        for (const point of points) {
            // Kameradan noktaya ray
            const direction = new THREE.Vector3()
                .subVectors(point, camera.position)
                .normalize();

            raycaster.set(camera.position, direction);
            raycaster.far = camera.position.distanceTo(point) - 0.1;

            // Sadece mesh'leri kontrol et (dimension'ları değil)
            const intersects = raycaster.intersectObjects(scene.scene.children, true)
                .filter(i => i.object.type === 'Mesh' && !i.object.userData.dimensionId);

            if (intersects.length > 0) {
                occludedCount++;
            }
        }

        // %50'den fazlası kapatılıyorsa occlusion var
        return occludedCount > points.length / 2;
    }

    /**
     * Overlap'i çöz - Offset artır
     */
    private resolveOverlap(dimension: Dimension, overlap: OverlapResult): boolean {
        // Şimdilik basit çözüm: offset artır
        // İleride daha akıllı çözümler eklenebilir
        const options = dimension.options;
        const currentOffset = options.offset || 8;

        // Yeni offset hesapla
        for (let i = 1; i <= this.MAX_OFFSET_ITERATIONS; i++) {
            const newOffset = currentOffset + i * 5;

            // Offset'i uygula ve tekrar kontrol et
            // (Bu gerçek implementasyonda dimension.setOffset() çağrılır)
        }

        // Çözüm bulundu varsay (gerçek implementasyonda kontrol edilmeli)
        return true;
    }

    /**
     * Occlusion'ı çöz - Alternatif yerleşim
     */
    private resolveOcclusion(dimension: Dimension): boolean {
        // Alternatif yerleşim öner
        const suggestions = this.suggestPlacements(dimension);

        if (suggestions.length > 0) {
            // En iyi öneriyi uygula
            return true;
        }

        return false;
    }

    /**
     * Yerleşim önerileri oluştur
     */
    suggestPlacements(dimension: Dimension): PlacementSuggestion[] {
        const suggestions: PlacementSuggestion[] = [];
        const baseOffset = dimension.options.offset || 8;

        // Farklı offset ve açı kombinasyonları dene
        const offsets = [baseOffset, baseOffset * 1.5, baseOffset * 2, baseOffset * 0.5];
        const angles = [0, 45, 90, 135, 180, 225, 270, 315];
        const placements: TextPlacement[] = [
            TextPlacement.ABOVE,
            TextPlacement.BELOW,
            TextPlacement.LEFT,
            TextPlacement.RIGHT
        ];

        for (const offset of offsets) {
            for (const angle of angles) {
                for (const textPlacement of placements) {
                    // Skor hesapla (düşük overlap, düşük occlusion = yüksek skor)
                    const score = this.calculatePlacementScore(dimension, offset, angle, textPlacement);

                    suggestions.push({
                        offset,
                        angle,
                        textPlacement,
                        score
                    });
                }
            }
        }

        // Skora göre sırala (yüksekten düşüğe)
        suggestions.sort((a, b) => b.score - a.score);

        return suggestions.slice(0, 5); // En iyi 5 öneri
    }

    /**
     * Yerleşim skoru hesapla
     */
    private calculatePlacementScore(
        dimension: Dimension,
        offset: number,
        angle: number,
        textPlacement: TextPlacement
    ): number {
        let score = 1.0;

        // Varsayılan offset'e yakınlık bonus
        const defaultOffset = 8;
        const offsetDiff = Math.abs(offset - defaultOffset) / defaultOffset;
        score -= offsetDiff * 0.2;

        // 0 derece tercih edilir
        const anglePenalty = Math.min(angle, 360 - angle) / 360;
        score -= anglePenalty * 0.1;

        // ABOVE ve CENTER tercih edilir
        if (textPlacement === TextPlacement.ABOVE || textPlacement === TextPlacement.CENTER) {
            score += 0.1;
        }

        return Math.max(0, Math.min(1, score));
    }

    /**
     * Dimension bounds al
     */
    private getBounds(dimension: Dimension): DimensionBounds | null {
        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera) {
            return null;
        }

        const camera = scene.anaGosterim.aktifKamera;
        const id = dimension.userData.dimensionId;

        // Önbellekten kontrol
        const cached = this.boundsCache.get(id);
        if (cached && this.lastCameraPosition.equals(camera.position)) {
            return cached;
        }

        // Hesapla
        const box = new THREE.Box3().setFromObject(dimension);
        if (box.isEmpty()) {
            return null;
        }

        // Screen space'e çevir
        const min2D = this.worldToScreen(box.min, camera);
        const max2D = this.worldToScreen(box.max, camera);

        const bounds: DimensionBounds = {
            min: new THREE.Vector2(Math.min(min2D.x, max2D.x), Math.min(min2D.y, max2D.y)),
            max: new THREE.Vector2(Math.max(min2D.x, max2D.x), Math.max(min2D.y, max2D.y)),
            center: new THREE.Vector2(
                (min2D.x + max2D.x) / 2,
                (min2D.y + max2D.y) / 2
            )
        };

        // Önbelleğe al
        this.boundsCache.set(id, bounds);
        this.lastCameraPosition.copy(camera.position);

        return bounds;
    }

    /**
     * World → Screen dönüşümü
     */
    private worldToScreen(point: THREE.Vector3, camera: THREE.Camera): THREE.Vector2 {
        const vector = point.clone().project(camera);
        return new THREE.Vector2(
            (vector.x + 1) / 2,
            (1 - vector.y) / 2
        );
    }

    /**
     * İki bounds çakışıyor mu?
     */
    private boundsOverlap(a: DimensionBounds, b: DimensionBounds): boolean {
        const threshold = this.OVERLAP_THRESHOLD / 1000; // Normalize

        return !(
            a.max.x + threshold < b.min.x ||
            a.min.x - threshold > b.max.x ||
            a.max.y + threshold < b.min.y ||
            a.min.y - threshold > b.max.y
        );
    }

    /**
     * Dimension'ın anahtar noktalarını al
     */
    private getDimensionKeyPoints(dimension: Dimension): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];

        // Dimension'ın çocuklarını tara
        dimension.traverse(child => {
            if (child instanceof THREE.Sprite) {
                points.push(child.position.clone().applyMatrix4(dimension.matrixWorld));
            }
        });

        // Merkez noktayı ekle
        const center = new THREE.Vector3();
        dimension.getWorldPosition(center);
        points.push(center);

        return points;
    }

    /**
     * Önbelleği temizle
     */
    clearCache(): void {
        this.boundsCache.clear();
    }

    /**
     * Temizlik
     */
    dispose(): void {
        this.boundsCache.clear();
    }
}
